import {
  CreateContextProvider,
  Form,
  useCreateController,
  useDataProvider,
  useNotify,
  useRefresh,
  useSaveContext,
  useTranslate,
} from 'react-admin';
import {
  Button as MUIButton,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import axios from 'axios';
import { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Resources } from '../../resource';
import {
  getDownloadUrl,
  getFileType,
  getMimeType,
  getMimeTypeSafe,
  getSignedUrl,
  uploadToSignedUrl,
} from 'src/pages/admin/services/cdnService';
import CdnUploadForm from './CdnUploadForm';

type UploadedItem = { filename: string; url?: string; error?: string };

const CdnSaveButton = ({ onDone, onPartialDone, isHashing, onFileUploaded, abortControllerRef }: { onDone: (cdnUrl: string) => void, onPartialDone: () => void, isHashing: boolean, onFileUploaded?: (item: UploadedItem) => void, abortControllerRef: React.MutableRefObject<AbortController | null> }) => {
  const t = useTranslate();
  const notify = useNotify();
  const { save } = useSaveContext();
  const dataProvider = useDataProvider();
  const { getValues, watch } = useFormContext();
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [folderProgress, setFolderProgress] = useState<{ current: number; total: number } | null>(null);
  const selectedFile = watch('_file');
  const selectedFolderFiles: File[] | undefined = watch('_folderFiles');

  const isFormReady = (!!selectedFile || !!(selectedFolderFiles && selectedFolderFiles.length > 0)) && !isHashing;

  const handleSave = async () => {
    const values = getValues();
    const folderFiles: File[] = values._folderFiles || [];
    const file: File | undefined = values._file;

    if (!values.pathPrefix?.trim()) {
      notify('module.cdnUpload.pathPrefixRequired', { type: 'error' });
      return;
    }

    // ── Folder upload mode ──────────────────────────────────────────────
    if (folderFiles.length > 0) {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      const { signal } = controller;

      setLoading(true);
      setFolderProgress({ current: 0, total: folderFiles.length });
      let completedCount = 0;

      const uploadFile = async (currentFile: File) => {
        // Each file gets its OWN presigned URL with its exact metadata.
        // size, mimeType, and filename must match what is actually uploaded,
        // otherwise the S3 signature will be invalid.
        const fileType = getFileType(currentFile.type);
        const mimeType = getMimeTypeSafe(currentFile); // never throws
        // webkitRelativePath: "icons/chains/eth.png"
        // parts[0]       = root folder name
        // parts[1..-2]   = subfolders
        // parts[-1]      = filename
        const fullRelative = currentFile.webkitRelativePath || currentFile.name;
        const parts = fullRelative.split('/');
        const fileName = parts[parts.length - 1];
        const rootFolder = parts.length > 1 ? parts[0] : '';
        const subFolders = parts.slice(1, -1); // subfolders between root and filename
        const filePathPrefix = [values.pathPrefix, rootFolder, ...subFolders].filter(Boolean).join('/');
        const relativePath = fileName;

        const signedUrlData = await getSignedUrl({
          filename: relativePath,
          fileType,
          mimeType,
          size: currentFile.size,        // exact size of this specific file
          pathPrefix: filePathPrefix,    // includes subfolders
          customFilename: relativePath,  // just the filename
          usePrivateBucket: values.usePrivateBucket ?? false,
        });

        const { uploadURL, file: fileInfo } = signedUrlData;

        // Upload using the exact same file object (Content-Type matches signature)
        await uploadToSignedUrl(uploadURL, currentFile, undefined, signal);

        await new Promise((resolve) => setTimeout(resolve, 500));
        const downloadData = await getDownloadUrl(fileInfo.id);
        const cdnUrl = downloadData?.url || fileInfo.cdnURL || fileInfo.publicURL;

        await dataProvider.create(Resources.cdnUpload, {
          data: { filename: relativePath, fileType, cdnUrl, size: currentFile.size },
        });

        completedCount += 1;
        setFolderProgress({ current: completedCount, total: folderFiles.length });
        onFileUploaded?.({ filename: relativePath, url: cdnUrl });
        return cdnUrl as string;
      };

      const { fulfilled, rejected } = await folderFiles.reduce<
        Promise<{ fulfilled: PromiseFulfilledResult<string>[]; rejected: PromiseRejectedResult[] }>
      >(
        async (accPromise, currentFile) => {
          const acc = await accPromise;
          if (signal.aborted) return acc;
          try {
            const cdnUrl = await uploadFile(currentFile);
            acc.fulfilled.push({ status: 'fulfilled', value: cdnUrl });
          } catch (err: any) {
            if (!axios.isCancel(err) && err?.name !== 'AbortError') {
              acc.rejected.push({ status: 'rejected', reason: err });
              const fileName = (currentFile.webkitRelativePath || currentFile.name).split('/').pop() ?? currentFile.name;
              onFileUploaded?.({ filename: fileName, error: err?.body?.message || err?.message || 'Upload failed' });
            }
          }
          return acc;
        },
        Promise.resolve({ fulfilled: [], rejected: [] })
      );

      setLoading(false);
      setFolderProgress(null);

      const successCount = fulfilled.length;
      const lastCdnUrl = fulfilled[fulfilled.length - 1]?.value ?? '';

      if (signal.aborted) {
        return;
      }

      if (successCount === folderFiles.length) {
        onDone(lastCdnUrl);
      } else if (successCount > 0) {
        notify('module.cdnUpload.folderUploadPartialSuccess', { type: 'warning', messageArgs: { success: successCount, total: folderFiles.length } });
        onPartialDone();
      } else {
        const firstError = rejected[0]?.reason;
        notify(firstError?.body?.message || firstError?.message || 'module.cdnUpload.uploadError', { type: 'error' });
      }
      return;
    }

    // ── Single file upload mode ─────────────────────────────────────────
    if (!file) {
      notify('module.cdnUpload.noFileSelected', { type: 'error' });
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    const { signal } = controller;

    setLoading(true);
    setUploadProgress(0);
    try {
      const fileType = getFileType(file.type);
      const mimeType = getMimeType(file);
      if (!save) {
        notify('module.cdnUpload.saveError', { type: 'error' });
        return;
      }

      // Get signed URL with exact file metadata
      const signedUrlData = await getSignedUrl({
        filename: file.name,
        fileType,
        mimeType,
        size: file.size,
        pathPrefix: values.pathPrefix,
        customFilename: values.customFilename || undefined,
        usePrivateBucket: values.usePrivateBucket ?? false,
      });

      const { uploadURL, file: fileInfo } = signedUrlData;

      await uploadToSignedUrl(uploadURL, file, setUploadProgress, signal);
      await new Promise((resolve) => setTimeout(resolve, 2000));
      const downloadData = await getDownloadUrl(fileInfo.id);
      const cdnUrl = downloadData?.url || fileInfo.cdnURL || fileInfo.publicURL;

      save(
        { filename: values.customFilename || file.name, fileType, cdnUrl, size: file.size },
        {
          onSuccess: () => { onFileUploaded?.({ filename: values.customFilename || file.name, url: cdnUrl }); onDone(cdnUrl); },
          onError: (error: any) => {
            onFileUploaded?.({ filename: values.customFilename || file.name, error: error?.body?.message || 'Save failed' });
            notify(error?.body?.message || 'module.cdnUpload.saveError', { type: 'error' });
          },
        }
      );
    } catch (error: any) {
      if (!axios.isCancel(error) && error?.name !== 'AbortError') {
        onFileUploaded?.({ filename: values.customFilename || file.name, error: error?.body?.message || error?.message || 'Upload failed' });
        notify(error?.body?.message || error?.message || 'module.cdnUpload.uploadError', {
          type: 'error',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="contained"
      onClick={handleSave}
      disabled={loading || !isFormReady || isHashing}
      startIcon={loading ? <CircularProgress size={16} /> : undefined}
    >
      {loading && folderProgress
        ? t('module.cdnUpload.uploadingFolder', { current: folderProgress.current, total: folderProgress.total })
        : loading
        ? `${t('module.cdnUpload.uploading')} ${uploadProgress}%`
        : t('module.cdnUpload.save')}
    </Button>
  );
};

// Main component
const CreateCdnUpload = ({
  onClose,
  onSuccess,
  onFileUploaded,
}: {
  onClose: () => void;
  onSuccess?: (cdnUrl: string) => void;
  onFileUploaded?: (item: UploadedItem) => void;
}) => {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [hashing, setHashing] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleCancel = () => {
    abortControllerRef.current?.abort();
    onClose();
  };

  const controller = useCreateController({
    resource: Resources.cdnUpload,
    mutationOptions: {
      onError: (error: any) => {
        notify(error?.body?.message || 'module.cdnUpload.saveError', { type: 'error' });
      },
    },
  });

  const handleDone = (cdnUrl: string) => {
    notify('module.cdnUpload.uploadSuccess', { type: 'success' });
    refresh();
    onClose();
    onSuccess?.(cdnUrl);
  };

  const handlePartialDone = () => {
    refresh();
    onClose();
  };

  return (
    <CreateContextProvider value={controller}>
      <Form disableInvalidFormNotification>
        <DialogTitle>{t('module.cdnUpload.uploadFile')}</DialogTitle>
        <DialogContent>
          <CdnUploadForm onHashingChange={setHashing} isHashing={hashing}/>
        </DialogContent>
        <DialogActions>
          <MUIButton variant="outlined" onClick={handleCancel}>
            {t('ra.action.cancel')}
          </MUIButton>
          <CdnSaveButton onDone={handleDone} onPartialDone={handlePartialDone} isHashing={hashing} onFileUploaded={onFileUploaded} abortControllerRef={abortControllerRef}/>
        </DialogActions>
      </Form>
    </CreateContextProvider>
  );
};

export default CreateCdnUpload;
